// ABOUTME: The 5D summary as data: five compass rings with this crossing's wedges lit, and the THIS CROSSING card rows.
// ABOUTME: Pure, so the drawing and the card are built from one derivation and cannot drift apart.

import { RepairStatus } from '../../../../types/api';
import type { CrossingDto, CrossingStormDto } from '../../../../types/api';
import { NO_STORM } from '../clouds/noStorm';
import { guidingTruthFor } from '../sky/guidingTruth';

const NOT_SURE = 'not_sure';

// Every set is optional: a partly loaded library draws the rings it has and leaves the others empty.
export interface SummaryContent {
  feelings?: Record<string, { code: string; name: string }>;
  storms?: Record<string, CrossingStormDto>;
  weatherPatterns?: Record<string, { code: string; name: string }>;
  reachTypes?: Record<string, { code: string; name: string }>;
}

export type SummaryRingKey = 'feeling' | 'storm' | 'weather' | 'reached' | 'nextMove';

export interface SummaryWedge {
  code: string;
  label: string;
  lit: boolean;
}

export interface SummaryRing {
  key: SummaryRingKey;
  wedges: SummaryWedge[];
  rotation: number; // degrees, so the first lit wedge sits at north
}

export interface SummaryRow {
  label: string;
  value: string;
}

export interface Summary {
  rings: SummaryRing[];
  rows: SummaryRow[];
  truth: string;
  childLine: string | null;
}

// The compass asset's reached ring runs clockwise in this order, which is not the library's seed order.
export const REACHED_RING_ORDER = ['trust', 'thing_space', 'needed_doing', 'person'];

const MADE_RIGHT_TEXT: Record<RepairStatus, string> = {
  [RepairStatus.Pending]: 'still open',
  [RepairStatus.Complete]: 'made right',
  [RepairStatus.CompleteForgiven]: 'made right — forgiven',
  [RepairStatus.CompleteNoMendNeeded]: 'no mend was needed',
};

const KULEANA_TEXT = 'Added to Kuleana list';
const NO_READING = '—';

function buildRing(key: SummaryRingKey, items: { code: string; label: string }[], litCodes: string[]): SummaryRing {
  const wedges = items.map(item => ({ ...item, lit: litCodes.includes(item.code) }));
  const firstLit = wedges.findIndex(wedge => wedge.lit);
  const rotation = firstLit <= 0 ? 0 : -firstLit * (360 / wedges.length);
  return { key, wedges, rotation };
}

function reachedItems(reachTypes: Record<string, { code: string; name: string }>) {
  const rank = (code: string) => {
    const index = REACHED_RING_ORDER.indexOf(code);
    return index === -1 ? REACHED_RING_ORDER.length : index;
  };
  return Object.values(reachTypes)
    .map((reach, libraryIndex) => ({ code: reach.code, label: reach.name, libraryIndex }))
    .sort((a, b) => rank(a.code) - rank(b.code) || a.libraryIndex - b.libraryIndex)
    .map(({ code, label }) => ({ code, label }));
}

export function buildSummary(crossing: CrossingDto, content: SummaryContent): Summary {
  const { feelings = {}, storms = {}, weatherPatterns = {}, reachTypes = {} } = content;
  const reachCodes = (crossing.reaches ?? []).map(reach => reach.reachCode);
  // Not sure and No Storm are the absence of a reading, so they have no wedge to light.
  const stormCodes = crossing.storm && crossing.storm !== NO_STORM ? [crossing.storm] : [];
  const stormList = Object.values(storms).filter(storm => storm.code !== NO_STORM);

  const rings = [
    buildRing('feeling', Object.values(feelings).filter(feeling => feeling.code !== NOT_SURE).map(feeling => ({ code: feeling.code, label: feeling.name })), crossing.feeling ? [crossing.feeling] : []),
    buildRing('storm', stormList.map(storm => ({ code: storm.code, label: storm.name })), stormCodes),
    buildRing('weather', Object.values(weatherPatterns).map(pattern => ({ code: pattern.code, label: pattern.name })), crossing.weather ? [crossing.weather] : []),
    buildRing('reached', reachedItems(reachTypes), reachCodes),
    buildRing('nextMove', stormList.map(storm => ({ code: storm.code, label: storm.houseName ?? storm.name })), stormCodes),
  ];

  const storm = crossing.storm ? storms[crossing.storm] : undefined;
  const reachedNames = reachCodes.map(code => reachTypes[code]?.name).filter((name): name is string => !!name);
  const stormValue = !crossing.storm ? NO_READING : crossing.storm === NO_STORM ? 'No Storm' : storm?.name ?? NO_READING;
  const madeRight = crossing.makeItRightListId != null
    ? KULEANA_TEXT
    : MADE_RIGHT_TEXT[crossing.repairStatus] ?? MADE_RIGHT_TEXT[RepairStatus.Pending];

  const rows: SummaryRow[] = [
    { label: 'Feeling', value: (crossing.feeling && crossing.feeling !== NOT_SURE && feelings[crossing.feeling]?.name) || NO_READING },
    { label: 'Weather response', value: (crossing.weather && weatherPatterns[crossing.weather]?.name) || NO_READING },
    { label: 'Storm', value: stormValue },
    { label: 'Reached', value: reachedNames.length > 0 ? reachedNames.join(', ') : 'Nothing reached' },
    { label: 'Made right', value: madeRight },
  ];

  const childLine = storm && crossing.storm !== NO_STORM && storm.childTruth?.trim() ? storm.childTruth : null;

  return { rings, rows, truth: guidingTruthFor(storm, crossing.storm), childLine };
}
